
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { InternalCandidate } from "../InternalCandidates";
import { supabase } from "@/integrations/supabase/client";

interface AddCandidateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: (candidate: Omit<InternalCandidate, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => void;
  nextSN: number;
}

export function AddCandidateDialog({
  open,
  onOpenChange,
  onAdd,
  nextSN
}: AddCandidateDialogProps) {
  const [formData, setFormData] = useState({
    role: '',
    country: '',
    city: '',
    candidate_name: '',
    skillset: '',
    years_experience: 0,
    linkedin_url: '',
    past_projects: '',
    project_id: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const getNextSN = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return 1;

      const { data, error } = await supabase
        .from('internal_candidates')
        .select('sn')
        .eq('user_id', user.id)
        .order('sn', { ascending: false })
        .limit(1);

      if (error) {
        console.error('Error getting max SN:', error);
        return 1;
      }

      return data && data.length > 0 ? data[0].sn + 1 : 1;
    } catch (error) {
      console.error('Error in getNextSN:', error);
      return 1;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.candidate_name || !formData.role || !formData.country || !formData.city) {
      return;
    }

    setIsSubmitting(true);
    try {
      const nextSN = await getNextSN();
      await onAdd({
        sn: nextSN,
        ...formData
      });
      // Reset form
      setFormData({
        role: '',
        country: '',
        city: '',
        candidate_name: '',
        skillset: '',
        years_experience: 0,
        linkedin_url: '',
        past_projects: '',
        project_id: ''
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      role: '',
      country: '',
      city: '',
      candidate_name: '',
      skillset: '',
      years_experience: 0,
      linkedin_url: '',
      past_projects: '',
      project_id: ''
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-secondary border-primary/20">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-primary">
            Add New Candidate
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="role" className="text-white text-sm">Role</Label>
            <Input
              id="role"
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              className="bg-secondary/30 border-primary/20 text-white"
              placeholder="e.g., Software Engineer"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="country" className="text-white text-sm">Country</Label>
              <Input
                id="country"
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                className="bg-secondary/30 border-primary/20 text-white"
                placeholder="e.g., USA"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="city" className="text-white text-sm">City</Label>
              <Input
                id="city"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="bg-secondary/30 border-primary/20 text-white"
                placeholder="e.g., New York"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="candidate_name" className="text-white text-sm">Candidate Name</Label>
              <Input
                id="candidate_name"
                value={formData.candidate_name}
                onChange={(e) => setFormData({ ...formData, candidate_name: e.target.value })}
                className="bg-secondary/30 border-primary/20 text-white"
                placeholder="e.g., John Doe"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="years_experience" className="text-white text-sm">Years of Experience</Label>
              <Input
                id="years_experience"
                type="number"
                value={formData.years_experience}
                onChange={(e) => setFormData({ ...formData, years_experience: parseInt(e.target.value) || 0 })}
                className="bg-secondary/30 border-primary/20 text-white"
                min="0"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="skillset" className="text-white text-sm">Skillset</Label>
            <Input
              id="skillset"
              value={formData.skillset}
              onChange={(e) => setFormData({ ...formData, skillset: e.target.value })}
              className="bg-secondary/30 border-primary/20 text-white"
              placeholder="e.g., React, TypeScript, Node.js"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="linkedin_url" className="text-white text-sm">LinkedIn URL</Label>
            <Input
              id="linkedin_url"
              type="url"
              value={formData.linkedin_url}
              onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
              className="bg-secondary/30 border-primary/20 text-white"
              placeholder="https://linkedin.com/in/username"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="past_projects" className="text-white text-sm">Past Projects</Label>
            <Textarea
              id="past_projects"
              value={formData.past_projects}
              onChange={(e) => setFormData({ ...formData, past_projects: e.target.value })}
              className="bg-secondary/30 border-primary/20 text-white h-20"
              placeholder="Brief description of relevant past projects"
            />
          </div>

          <DialogFooter className="flex justify-end gap-2 pt-4">
            <Button 
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isSubmitting}
              className="px-8"
            >
              Cancel
            </Button>
            <Button 
              type="submit"
              disabled={isSubmitting || !formData.candidate_name || !formData.role || !formData.country || !formData.city}
              className="bg-primary hover:bg-primary/90 text-black px-8"
            >
              {isSubmitting ? 'Adding...' : 'Add Candidate'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
